// covered-by: frontends/carrie/src/ask-v2/routes/__tests__/settingsRoutes.test.ts
// eslint-disable-next-line import/named
import { createRoute, redirect } from "@tanstack/react-router";
import { z } from "zod";

import { askV2AuthLayoutRoute } from "./authLayout";

import PendingComponent from "../../components/PendingComponent";
import AuditLogView from "../../routes/roi/routes/audit-log/view";
import ROIView from "../../routes/roi/view";
import { Settings } from "../../routes/settings/route";
import { ArchivedUsers } from "../../routes/settings/routes/archivedUsersRoute";
import { EnterpriseSettingsView } from "../../routes/settings/routes/EnterpriseSettingsView";
import { IntegrationSettings } from "../../routes/settings/routes/integrationSettingsRoute";
import { knowledgeDatabaseSearchSchema } from "../../routes/settings/routes/knowledge-database/search";
import KnowledgeDatabaseView from "../../routes/settings/routes/knowledge-database/view";
import { KnowledgeSettings } from "../../routes/settings/routes/knowledgeSettingRoute";
import { NotificationsSettingsView } from "../../routes/settings/routes/NotificationsSettingsView";
import { Users } from "../../routes/settings/routes/usersRoute";
import { WarholOutputSettings } from "../../routes/settings/routes/warholOutputSettingsRoute";
import ErrorComponent from "../../routes/tickets/routes/ticket/components/ErrorComponent";
import {
  canAccessAskPuntt,
  canAccessKnowledgeDatabase,
  canEnterpriseAdmin,
  canManageBrandGuidelines,
  canManageUsers,
  isMeaningfulGigsOrDemo,
  isMeaningfulGigsRole,
  requiresAuth,
} from "../../utils/auth";
import { ASK_V2_HOME_PATH } from "../paths";

function denyToAskHome() {
  throw redirect({ to: ASK_V2_HOME_PATH });
}

export const askV2SettingsRoute = createRoute({
  getParentRoute: () => askV2AuthLayoutRoute,
  path: "settings",
  component: Settings,
});

export const askV2EnterpriseSettingsRoute = createRoute({
  getParentRoute: () => askV2AuthLayoutRoute,
  path: "enterprise-settings",
  beforeLoad() {
    requiresAuth();
    if (!isMeaningfulGigsRole()) denyToAskHome();
  },
  component: EnterpriseSettingsView,
});

export const askV2KnowledgeSettingsRoute = createRoute({
  getParentRoute: () => askV2AuthLayoutRoute,
  path: "knowledge-settings",
  beforeLoad() {
    requiresAuth();
    if (!canManageBrandGuidelines()) denyToAskHome();
  },
  component: KnowledgeSettings,
});

export const askV2OutputGuidanceRoute = createRoute({
  getParentRoute: () => askV2AuthLayoutRoute,
  path: "output-guidance",
  beforeLoad() {
    requiresAuth();
    if (!canEnterpriseAdmin()) denyToAskHome();
  },
  component: WarholOutputSettings,
});

export const askV2KnowledgeDatabaseRoute = createRoute({
  getParentRoute: () => askV2AuthLayoutRoute,
  path: "settings/knowledge-database",
  // The SAME schema the legacy route validates with, not a copy. The copy
  // drifted: `cite` was added there and missing here, so a citation deep link
  // worked on the surface almost nobody uses and did nothing on the one they do.
  validateSearch: (search: Record<string, unknown>) =>
    knowledgeDatabaseSearchSchema.parse(search),
  beforeLoad() {
    requiresAuth();
    if (!canAccessKnowledgeDatabase()) denyToAskHome();
  },
  component: KnowledgeDatabaseView,
  pendingComponent: PendingComponent,
  errorComponent: ErrorComponent,
  gcTime: 0,
  shouldReload: false,
  pendingMs: 1000,
  pendingMinMs: 500,
});

export const askV2IntegrationSettingsRoute = createRoute({
  getParentRoute: () => askV2AuthLayoutRoute,
  path: "integration-settings",
  beforeLoad() {
    requiresAuth();
    if (!canManageUsers()) denyToAskHome();
  },
  component: IntegrationSettings,
});

export const askV2NotificationsSettingsRoute = createRoute({
  getParentRoute: () => askV2AuthLayoutRoute,
  path: "notifications-settings",
  beforeLoad() {
    requiresAuth();
    if (!canAccessAskPuntt()) denyToAskHome();
  },
  component: NotificationsSettingsView,
});

export const askV2UsersRoute = createRoute({
  getParentRoute: () => askV2AuthLayoutRoute,
  path: "users",
  beforeLoad() {
    requiresAuth();
    if (!canManageUsers()) denyToAskHome();
  },
  component: Users,
});

export const askV2ArchivedUsersRoute = createRoute({
  getParentRoute: () => askV2AuthLayoutRoute,
  path: "archived-users",
  beforeLoad() {
    requiresAuth();
    if (!canManageUsers()) denyToAskHome();
  },
  component: ArchivedUsers,
});

export const askV2RoiRoute = createRoute({
  getParentRoute: () => askV2AuthLayoutRoute,
  path: "roi",
  validateSearch(search: Record<string, unknown>) {
    const schema = z.object({
      startDate: z.string().optional(),
      endDate: z.string().optional(),
    });
    return schema.parse(search);
  },
  beforeLoad() {
    if (!isMeaningfulGigsOrDemo()) denyToAskHome();
  },
  loaderDeps({ search }) {
    return {
      startDate: search.startDate,
      endDate: search.endDate,
    };
  },
  async loader({ deps }) {
    return deps;
  },
  component: ROIView,
  pendingComponent: PendingComponent,
  gcTime: 0,
  shouldReload: false,
  pendingMs: 0,
  pendingMinMs: 500,
});

export const askV2RoiAuditRoute = createRoute({
  getParentRoute: () => askV2AuthLayoutRoute,
  path: "roi/audit",
  validateSearch(search: Record<string, unknown>) {
    const schema = z.object({
      page: z.coerce.number().optional(),
      atomicTasks: z.string().optional(),
      startDate: z.string().optional(),
      endDate: z.string().optional(),
      atomicTaskFilterMode: z
        .enum(["includes", "has_issues"])
        .optional()
        .default("includes"),
    });
    return schema.parse(search);
  },
  // The SAME gate as askV2RoiRoute above, for the same reason the ROI
  // dashboard route uses it: the only way onto this page is the per-row
  // audit-log link in the dashboard's AutomationTable, which has no
  // permission check of its own — everyone who can see the dashboard sees
  // the link. Gating the destination more narrowly than the page that renders
  // the link just hands demo-enterprise personas (not MG, but allowed onto the
  // dashboard by isMeaningfulGigsOrDemo) a visible count that bounces them
  // back to ask home when they click it.
  //
  // Widening is in contract for isMeaningfulGigsOrDemo — see its doc comment
  // in utils/auth.ts: it adds only users of an `isDemo` tenant, never a real
  // customer, and this page is a read-only drill-down of data the dashboard
  // already shows them. Restore isMeaningfulGigsRole() here and
  // settingsRoutes.test.ts reds.
  beforeLoad() {
    if (!isMeaningfulGigsOrDemo()) denyToAskHome();
  },
  loaderDeps({ search }) {
    return search;
  },
  async loader({ deps }) {
    return {
      page: deps.page,
      atomicTasks: deps.atomicTasks,
      startDate: deps.startDate,
      endDate: deps.endDate,
      atomicTaskFilterMode: deps.atomicTaskFilterMode,
    };
  },
  component: AuditLogView,
  pendingComponent: PendingComponent,
  gcTime: 0,
  shouldReload: false,
  pendingMs: 0,
  pendingMinMs: 500,
});

export const askV2SettingsRoutes = [
  askV2SettingsRoute,
  askV2EnterpriseSettingsRoute,
  askV2KnowledgeSettingsRoute,
  askV2OutputGuidanceRoute,
  askV2KnowledgeDatabaseRoute,
  askV2IntegrationSettingsRoute,
  askV2NotificationsSettingsRoute,
  askV2UsersRoute,
  askV2ArchivedUsersRoute,
  askV2RoiRoute,
  askV2RoiAuditRoute,
];
